import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  isDesktop: boolean;
  onClose: () => void;
}

type IconProps = { className?: string };

const DashboardIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 12h7V3H3z" />
    <path d="M14 21h7v-9h-7z" />
    <path d="M14 3h7v5h-7z" />
    <path d="M3 16h7v5H3z" />
  </svg>
);

const PatientIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <circle cx="17" cy="13" r="1" />
  </svg>
);

const AppointmentIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="4" />
    <path d="M8 2v4M16 2v4M3 10h18" />
    <path d="m9 16 2 2 4-4" />
  </svg>
);

const MedicalRecordIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 3h10l4 4v13a1 1 0 0 1-1 1H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    <path d="M15 3v4h4M12 11v6M9 14h6" />
  </svg>
);

const InvoiceIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
  </svg>
);

const InventoryIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M12 22V12M22 9l-10 5L2 9" />
  </svg>
);

const UsersIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const BotIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8.01" y2="16" />
    <line x1="16" y1="16" x2="16.01" y2="16" />
  </svg>
);

const navigation = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: DashboardIcon },
  { to: '/patients', labelKey: 'nav.patients', icon: PatientIcon },
  { to: '/appointments', labelKey: 'nav.appointments', icon: AppointmentIcon },
  { to: '/medical-records', labelKey: 'nav.medicalRecords', icon: MedicalRecordIcon },
  { to: '/invoices', labelKey: 'nav.invoices', icon: InvoiceIcon },
  { to: '/inventory', labelKey: 'nav.inventory', icon: InventoryIcon },
  { to: '/users', labelKey: 'nav.users', icon: UsersIcon },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, isDesktop, onClose }) => {
  const { user, } = useAuth();
  const { t } = useTranslation();

  const handleNavClick = () => {
    if (!isDesktop) {
      onClose();
    }
  };

  const roleLabel = user?.role
    ? t(`roles.${user.role}`, { defaultValue: t('roles.fallback') })
    : t('roles.fallback');

  const sidebarClasses = [
    'fixed lg:relative inset-y-0 left-0 z-40 w-72 transform transition-transform duration-300',
    'bg-gradient-to-b from-white via-white to-slate-50 border-r border-slate-200 shadow-xl',
    isOpen ? 'translate-x-0' : '-translate-x-full',
    isDesktop && isOpen ? 'lg:translate-x-0' : '',
  ].join(' ');

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && !isDesktop && (
        <div
          className="fixed inset-0 z-30 bg-neutral/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={sidebarClasses}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <NavLink
            to="/dashboard"
            onClick={handleNavClick}
            className="flex items-center gap-3 border-b border-slate-200 px-6 py-5 hover:bg-indigo-50/50 transition-colors"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-2xl text-white shadow-lg">
              🐾
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800">{t('common.appName')}</p>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                {t('layout.sidebarBadge')}
              </p>
            </div>
          </NavLink>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        isActive
                          ? 'flex items-center gap-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-white shadow-lg font-semibold transition-all'
                          : 'flex items-center gap-3 rounded-xl px-4 py-3 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-all font-medium'
                      }
                    >
                      <Icon className="h-5 w-5" />
                      <span>{t(item.labelKey)}</span>
                    </NavLink>
                  </li>
                );
              })}

              <div className="my-4 h-px bg-slate-200"></div>

              <li>
                <a
                  href="/ai-chat-full"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-cyan-50 to-violet-50 px-4 py-3 text-cyan-700 hover:from-cyan-100 hover:to-violet-100 transition-all font-medium"
                >
                  <BotIcon className="h-5 w-5" />
                  <span>AI Assistant</span>
                  <span className="ml-auto rounded-full bg-cyan-600 px-2 py-0.5 text-xs font-semibold text-white">New</span>
                </a>
              </li>
            </ul>
          </nav>

          {/* User Info */}
          <div className="border-t border-slate-200 bg-slate-50/50 p-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                {t('layout.loggedInAs')}
              </p>
              <p className="mt-2 text-base font-bold text-slate-800">
                {user?.name || t('layout.welcomeAnonymous')}
              </p>
              <span className="mt-2 inline-block rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">{roleLabel}</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
